import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, Clock, ListChecks } from 'lucide-react';

import { useResumoJobs } from '../../../hooks/useMonitoramentoQuery';

/**
 * KPIs agregados das três filas de job — exibidos no topo da aba Jobs.
 * Mostra "% sucesso 1h", "% sucesso 24h", pendentes e em execução. Quando o
 * denominador de sucesso é zero (ninguém terminou na janela), exibe "—" em
 * vez de fingir 100% — mesma regra do SLI derivado para baixo tráfego.
 */
export function JobsKpiHeader() {
  const { data, isLoading } = useResumoJobs(30_000);

  const sucesso1h = formatPercent(data?.percentualSucesso1h);
  const sucesso24h = formatPercent(data?.percentualSucesso24h);
  const pendentes = data?.pendentes ?? 0;
  const emExecucao = data?.emExecucao ?? 0;
  const total1h = (data?.sucesso1h ?? 0) + (data?.falha1h ?? 0);
  const total24h = (data?.sucesso24h ?? 0) + (data?.falha24h ?? 0);

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <KpiCard
        icon={<CheckCircle2 className="size-5 text-muted-foreground" />}
        label="Sucesso 1h"
        value={isLoading ? '…' : sucesso1h}
        dica={`${total1h} ${total1h === 1 ? 'job finalizado' : 'jobs finalizados'} na última hora`}
      />
      <KpiCard
        icon={<CheckCircle2 className="size-5 text-muted-foreground" />}
        label="Sucesso 24h"
        value={isLoading ? '…' : sucesso24h}
        dica={`${total24h} ${total24h === 1 ? 'job finalizado' : 'jobs finalizados'} nas últimas 24h`}
      />
      <KpiCard
        icon={<Clock className="size-5 text-muted-foreground" />}
        label="Pendentes"
        value={isLoading ? '…' : String(pendentes)}
        dica="Jobs aguardando processamento"
      />
      <KpiCard
        icon={<ListChecks className="size-5 text-muted-foreground" />}
        label="Em execução"
        value={isLoading ? '…' : String(emExecucao)}
        dica="Jobs processando agora"
      />
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  dica,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  dica: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-2 px-3 py-2">
        {icon}
        <div className="min-w-0">
          <div className="break-words text-xs text-muted-foreground sm:text-[11px]" title={dica}>
            {label}
          </div>
          <div className="break-words text-base font-semibold leading-tight">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatPercent(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) {
    return '—';
  }
  if (valor >= 99.95) return '100%';
  if (valor < 10) return `${valor.toFixed(1)}%`;
  return `${Math.round(valor)}%`;
}
